import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable, runInAction } from 'mobx';

import type { RootStore } from './RootStore';
import type {
  TranslationHandlerRecord,
  TranslationProviderKey,
} from '@src/types.ts/translationHandlerRecord';

export type TranslationHandlerLoadingStatus =
  | 'idle'
  | 'loading'
  | 'loaded'
  | 'error';

export class TranslationHandlerStore {
  activeHandlers: TranslationHandlerRecord[] = [];
  loadingStatus: TranslationHandlerLoadingStatus = 'idle';

  private pendingLoad: Promise<void> | null = null;
  private logger = LogManager.getLogger('store:TranslationHandlerStore');

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
  }

  async loadAll(): Promise<void> {
    if (this.loadingStatus === 'loaded') return;
    if (this.pendingLoad) return this.pendingLoad;

    runInAction(() => {
      this.loadingStatus = 'loading';
    });

    const run = async (): Promise<void> => {
      try {
        const handlers =
          await this.rootStore.datoClientStore.translationHandlerRepository.getAllActiveHandlers();
        runInAction(() => {
          this.activeHandlers = handlers;
          this.loadingStatus = 'loaded';
        });
      } catch {
        runInAction(() => {
          this.loadingStatus = 'error';
        });
      } finally {
        this.pendingLoad = null;
      }
    };

    this.pendingLoad = run();
    return this.pendingLoad;
  }

  getByInternalName(
    internalName: string,
  ): TranslationHandlerRecord | undefined {
    return this.activeHandlers.find(h => h.internalName === internalName);
  }

  getByProvider(provider: TranslationProviderKey): TranslationHandlerRecord[] {
    return this.activeHandlers.filter(h => h.provider === provider);
  }

  // Returns the first handler flagged is_default. If multiple records happen
  // to have is_default === true (admin-UI bug, manual API edit, etc.),
  // deterministically pick the first and log a warn so we notice the drift.
  // Returns undefined if no default is set.
  //
  // "First" means first in `activeHandlers` order, which is GraphQL response
  // order for `allTranslationHandlers` (Dato returns records by id ascending
  // by default). This tie-break is deterministic for any given Dato state but
  // may feel arbitrary if the warn fires — the underlying issue is always
  // "multiple records flagged default", not "wrong record picked".
  getDefault(): TranslationHandlerRecord | undefined {
    const defaults = this.activeHandlers.filter(h => h.isDefault);
    if (defaults.length > 1) {
      this.logger.warn(
        'Multiple translation handlers have is_default: true; picking the first deterministically. Admin UI should enforce mutual exclusion.',
        { count: defaults.length, picked: defaults[0].internalName },
      );
    }
    return defaults[0];
  }
}
